import React from 'react';

/**
 * Static SVG illustrations for each Sudoku tutorial technique (Levels 1–17).
 * Each diagram renders a 9×9 grid, optionally highlighting:
 *   - `given`    — pre-filled numbers (shown bold/dark)
 *   - `focus`    — the cell(s) / unit the technique operates on (soft highlight)
 *   - `candidates` — penciled candidates in specific cells
 *   - `elim`     — cells where candidates get eliminated (struck-through)
 *   - `result`   — the cell(s) where the deduced value goes
 *
 * Each technique has its own hand-tuned example designed to be clear at a glance.
 * Candidate visibility is controlled via CandidatesContext (set by parent).
 */

const CandidatesContext = React.createContext(true);

const CELL = 36;
const GRID = CELL * 9;

function Grid({ children }) {
  return (
    <svg
      viewBox={`0 0 ${GRID + 2} ${GRID + 2}`}
      xmlns="http://www.w3.org/2000/svg"
      className="w-full max-w-md mx-auto"
      style={{ background: 'var(--background-surface)', borderRadius: 6 }}
    >
      {/* Cells background */}
      <g transform="translate(1,1)">{children}</g>
      {/* Thin grid lines */}
      <g stroke="var(--border-subtle)" strokeWidth="0.5" transform="translate(1,1)">
        {Array.from({ length: 10 }, (_, i) => (
          <React.Fragment key={`lines-${i}`}>
            <line x1={i * CELL} y1={0} x2={i * CELL} y2={GRID} />
            <line x1={0} y1={i * CELL} x2={GRID} y2={i * CELL} />
          </React.Fragment>
        ))}
      </g>
      {/* Thick box lines */}
      <g stroke="var(--text-secondary)" strokeWidth="1.5" fill="none" transform="translate(1,1)">
        {[0, 3, 6, 9].map((i) => (
          <React.Fragment key={`box-${i}`}>
            <line x1={i * CELL} y1={0} x2={i * CELL} y2={GRID} />
            <line x1={0} y1={i * CELL} x2={GRID} y2={i * CELL} />
          </React.Fragment>
        ))}
      </g>
    </svg>
  );
}

function Cell({ r, c, fill = 'transparent', children }) {
  return (
    <g transform={`translate(${c * CELL},${r * CELL})`}>
      <rect width={CELL} height={CELL} fill={fill} />
      {children}
    </g>
  );
}

function Given({ value }) {
  return (
    <text
      x={CELL / 2}
      y={CELL / 2 + 7}
      textAnchor="middle"
      fontSize="20"
      fontWeight="700"
      fill="var(--text-primary)"
      fontFamily="Helvetica Neue, Arial, sans-serif"
    >
      {value}
    </text>
  );
}

function Candidates({ values, strike = [], highlight = [] }) {
  const visible = React.useContext(CandidatesContext);
  if (!visible) return null;
  // render up to 9 candidates in 3x3 mini-grid
  return (
    <g>
      {values.map((v) => {
        const col = (v - 1) % 3;
        const row = Math.floor((v - 1) / 3);
        const isStruck = strike.includes(v);
        const isHi = highlight.includes(v);
        return (
          <g key={v} transform={`translate(${col * 12 + 2},${row * 10 + 4})`}>
            <text
              x={5}
              y={7}
              textAnchor="middle"
              fontSize="8"
              fontWeight={isHi ? '700' : '500'}
              fill={isHi ? 'var(--focus-accent, #FF9F0A)' : isStruck ? 'var(--text-disabled)' : 'var(--text-secondary)'}
              textDecoration={isStruck ? 'line-through' : 'none'}
              fontFamily="Helvetica Neue, Arial, sans-serif"
            >
              {v}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function Result({ value }) {
  return (
    <text
      x={CELL / 2}
      y={CELL / 2 + 7}
      textAnchor="middle"
      fontSize="20"
      fontWeight="700"
      fill="#34C759"
      fontFamily="Helvetica Neue, Arial, sans-serif"
    >
      {value}
    </text>
  );
}

// ============================================================================
// Per-technique illustrations
// ============================================================================

// LEVEL 0 — Reading Candidates (foundational lesson)
// Shows a realistic partial grid with the target cell R1C3 highlighted.
// Its candidates {1,2,4} are penciled in so the learner can see what
// "candidates" actually looks like on the board.
function Candidates0() {
  // A standard beginner-friendly layout. Row 0 has 5, 3, ., ., 7 — cell (0,2)
  // is empty and its candidates after scanning row/col/box are {1, 2, 4}.
  const givens = {
    '0,0': 5, '0,1': 3, '0,4': 7,
    '1,0': 6, '1,3': 1, '1,4': 9, '1,5': 5,
    '2,1': 9, '2,2': 8, '2,7': 6,
    '3,0': 8, '3,4': 6, '3,8': 3,
    '4,0': 4, '4,3': 8, '4,5': 3, '4,8': 1,
    '5,0': 7, '5,4': 2, '5,8': 6,
    '6,1': 6, '6,6': 2, '6,7': 8,
    '7,3': 4, '7,4': 1, '7,5': 9, '7,8': 5,
    '8,4': 8, '8,7': 7, '8,8': 9,
  };
  const target = [0, 2]; // R1C3 in human numbering

  return (
    <Grid>
      {/* Soft row/col/box highlights for the target cell */}
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`row-${c}`} r={target[0]} c={c} fill="rgba(0,122,255,0.06)" />
      ))}
      {Array.from({ length: 9 }, (_, r) => (
        <Cell key={`col-${r}`} r={r} c={target[1]} fill="rgba(255,159,10,0.06)" />
      ))}
      {/* Top-left 3x3 box */}
      {[0, 1, 2].map((r) =>
        [0, 1, 2].map((c) => (
          <Cell key={`box-${r}-${c}`} r={r} c={c} fill="rgba(175,82,222,0.06)" />
        ))
      )}
      {/* Given numbers */}
      {Object.entries(givens).map(([rc, v]) => {
        const [r, c] = rc.split(',').map(Number);
        return (
          <Cell key={rc} r={r} c={c}>
            <Given value={v} />
          </Cell>
        );
      })}
      {/* Target cell with candidates {1,2,4} */}
      <Cell r={target[0]} c={target[1]} fill="rgba(52,199,89,0.22)">
        <Candidates values={[1, 2, 4]} highlight={[1, 2, 4]} />
      </Cell>
    </Grid>
  );
}

// LEVEL 1 — Naked Single: cell in row/col/box where only one candidate remains
function NakedSingle() {
  const givens = {
    '0,0': 5, '0,1': 3, '0,4': 7,
    '1,0': 6, '1,3': 1, '1,4': 9, '1,5': 5,
    '2,1': 9, '2,2': 8, '2,7': 6,
    '3,0': 8, '3,4': 6, '3,8': 3,
    '4,0': 4, '4,3': 8, '4,5': 3, '4,8': 1,
  };
  // Target cell (4,4) — Naked Single = 5
  return (
    <Grid>
      {Object.entries(givens).map(([rc, v]) => {
        const [r, c] = rc.split(',').map(Number);
        return (
          <Cell key={rc} r={r} c={c}>
            <Given value={v} />
          </Cell>
        );
      })}
      {/* Highlighted row and col for target */}
      <Cell r={4} c={4} fill="rgba(52,199,89,0.18)">
        <Result value={5} />
      </Cell>
    </Grid>
  );
}

// LEVEL 2 — Hidden Single: value appears only once in row/col/box candidates
function HiddenSingle() {
  const givens = {
    '0,0': 2, '0,3': 6,
    '1,4': 9, '1,6': 7,
    '2,1': 5, '2,7': 2,
    '3,2': 3, '3,5': 1,
  };
  return (
    <Grid>
      {/* Highlight row 4 as the unit of interest */}
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`rh-${c}`} r={4} c={c} fill="rgba(0,122,255,0.08)" />
      ))}
      {Object.entries(givens).map(([rc, v]) => {
        const [r, c] = rc.split(',').map(Number);
        return (
          <Cell key={rc} r={r} c={c}>
            <Given value={v} />
          </Cell>
        );
      })}
      {/* Candidate cells in row 4 — only one has 8 as candidate */}
      <Cell r={4} c={1}><Candidates values={[1, 3, 5]} /></Cell>
      <Cell r={4} c={3}><Candidates values={[2, 4, 8]} highlight={[8]} /></Cell>
      <Cell r={4} c={6}><Candidates values={[1, 4, 5]} /></Cell>
      <Cell r={4} c={8}><Candidates values={[3, 4, 7]} /></Cell>
    </Grid>
  );
}

// LEVEL 3 — Naked Pair: two cells in a unit share exactly the same two candidates
function NakedPair() {
  return (
    <Grid>
      {/* Highlight row 2 */}
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`rh-${c}`} r={2} c={c} fill="rgba(0,122,255,0.08)" />
      ))}
      <Cell r={2} c={0}><Candidates values={[3, 7]} highlight={[3, 7]} /></Cell>
      <Cell r={2} c={3}><Candidates values={[3, 7]} highlight={[3, 7]} /></Cell>
      <Cell r={2} c={1}><Candidates values={[1, 3, 5, 7, 9]} strike={[3, 7]} /></Cell>
      <Cell r={2} c={2}><Candidates values={[3, 5, 7, 8]} strike={[3, 7]} /></Cell>
      <Cell r={2} c={5}>
        <Given value={4} />
      </Cell>
      <Cell r={2} c={7}>
        <Given value={6} />
      </Cell>
    </Grid>
  );
}

// LEVEL 4 — Hidden Pair: two candidates confined to the same two cells
function HiddenPair() {
  return (
    <Grid>
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`rh-${c}`} r={0} c={c} fill="rgba(0,122,255,0.08)" />
      ))}
      <Cell r={0} c={2}><Candidates values={[2, 4, 6, 8]} highlight={[2, 6]} /></Cell>
      <Cell r={0} c={4}><Candidates values={[2, 5, 6, 9]} highlight={[2, 6]} /></Cell>
      <Cell r={0} c={0}><Given value={5} /></Cell>
      <Cell r={0} c={1}><Given value={3} /></Cell>
      <Cell r={0} c={6}><Candidates values={[1, 4, 5, 9]} /></Cell>
      <Cell r={0} c={8}><Candidates values={[1, 4, 5]} /></Cell>
    </Grid>
  );
}

// LEVEL 5 — Pointing Pair: candidate in a box confined to one row/col
function PointingPair() {
  return (
    <Grid>
      {/* Highlight top-left box */}
      {[0, 1, 2].map((r) =>
        [0, 1, 2].map((c) => (
          <Cell key={`bh-${r}-${c}`} r={r} c={c} fill="rgba(175,82,222,0.10)" />
        ))
      )}
      {/* Row 1 highlighted as the "line" where candidate 7 is confined */}
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`rh-${c}`} r={1} c={c} fill={c < 3 ? 'rgba(255,159,10,0.22)' : 'rgba(255,159,10,0.08)'} />
      ))}
      <Cell r={1} c={0}><Candidates values={[2, 7, 8]} highlight={[7]} /></Cell>
      <Cell r={1} c={2}><Candidates values={[5, 7]} highlight={[7]} /></Cell>
      <Cell r={1} c={4}><Candidates values={[1, 3, 7]} strike={[7]} /></Cell>
      <Cell r={1} c={6}><Candidates values={[4, 7, 9]} strike={[7]} /></Cell>
      <Cell r={0} c={1}><Given value={3} /></Cell>
      <Cell r={2} c={1}><Given value={6} /></Cell>
    </Grid>
  );
}

// LEVEL 6 — Box-Line Reduction (Claiming)
function BoxLineReduction() {
  return (
    <Grid>
      {/* Highlight column 4 */}
      {Array.from({ length: 9 }, (_, r) => (
        <Cell key={`ch-${r}`} r={r} c={4} fill="rgba(255,159,10,0.10)" />
      ))}
      {/* Highlight middle box (rows 3-5, cols 3-5) stronger */}
      {[3, 4, 5].map((r) =>
        [3, 4, 5].map((c) => (
          <Cell key={`bh-${r}-${c}`} r={r} c={c} fill="rgba(175,82,222,0.12)" />
        ))
      )}
      <Cell r={3} c={4}><Candidates values={[2, 8]} highlight={[8]} /></Cell>
      <Cell r={4} c={4}><Candidates values={[5, 8]} highlight={[8]} /></Cell>
      <Cell r={3} c={3}><Candidates values={[1, 2, 5]} strike={[8]} /></Cell>
      <Cell r={4} c={5}><Candidates values={[2, 6]} strike={[8]} /></Cell>
      <Cell r={5} c={3}><Candidates values={[2, 6, 9]} strike={[8]} /></Cell>
      <Cell r={5} c={4}><Given value={3} /></Cell>
      <Cell r={5} c={5}><Given value={7} /></Cell>
    </Grid>
  );
}

// LEVEL 7 — X-Wing: 4 corners across 2 rows + 2 columns
function XWing() {
  return (
    <Grid>
      {/* Rows 1 and 6 highlighted */}
      {Array.from({ length: 9 }, (_, c) => (
        <React.Fragment key={`r-${c}`}>
          <Cell r={1} c={c} fill="rgba(0,122,255,0.06)" />
          <Cell r={6} c={c} fill="rgba(0,122,255,0.06)" />
        </React.Fragment>
      ))}
      {/* Columns 2 and 7 highlighted */}
      {Array.from({ length: 9 }, (_, r) => (
        <React.Fragment key={`col-${r}`}>
          <Cell r={r} c={2} fill="rgba(255,159,10,0.06)" />
          <Cell r={r} c={7} fill="rgba(255,159,10,0.06)" />
        </React.Fragment>
      ))}
      {/* 4 corners of X-Wing — strong highlight */}
      {[[1, 2], [1, 7], [6, 2], [6, 7]].map(([r, c]) => (
        <Cell key={`xw-${r}-${c}`} r={r} c={c} fill="rgba(52,199,89,0.30)">
          <Candidates values={[5]} highlight={[5]} />
        </Cell>
      ))}
      {/* Example eliminations */}
      <Cell r={3} c={2}><Candidates values={[1, 5, 9]} strike={[5]} /></Cell>
      <Cell r={4} c={7}><Candidates values={[5, 8]} strike={[5]} /></Cell>
    </Grid>
  );
}

// LEVEL 8 — Swordfish: 3 rows × 3 cols
function Swordfish() {
  const cells = [
    [0, 1], [0, 4], [0, 6],
    [4, 1], [4, 4], [4, 6],
    [7, 1], [7, 4], [7, 6],
  ];
  return (
    <Grid>
      {[0, 4, 7].map((r) =>
        Array.from({ length: 9 }, (_, c) => (
          <Cell key={`r-${r}-${c}`} r={r} c={c} fill="rgba(0,122,255,0.05)" />
        ))
      )}
      {[1, 4, 6].map((c) =>
        Array.from({ length: 9 }, (_, r) => (
          <Cell key={`col-${c}-${r}`} r={r} c={c} fill="rgba(255,159,10,0.05)" />
        ))
      )}
      {cells.map(([r, c]) => (
        <Cell key={`sw-${r}-${c}`} r={r} c={c} fill="rgba(52,199,89,0.30)">
          <Candidates values={[2]} highlight={[2]} />
        </Cell>
      ))}
      <Cell r={2} c={1}><Candidates values={[2, 5]} strike={[2]} /></Cell>
      <Cell r={5} c={4}><Candidates values={[2, 7]} strike={[2]} /></Cell>
    </Grid>
  );
}

// LEVEL 9 — XY-Wing
function XYWing() {
  return (
    <Grid>
      <Cell r={4} c={4} fill="rgba(175,82,222,0.25)"><Candidates values={[1, 2]} highlight={[1, 2]} /></Cell>
      <Cell r={4} c={7} fill="rgba(0,122,255,0.22)"><Candidates values={[1, 3]} highlight={[1, 3]} /></Cell>
      <Cell r={7} c={4} fill="rgba(0,122,255,0.22)"><Candidates values={[2, 3]} highlight={[2, 3]} /></Cell>
      <Cell r={7} c={7} fill="rgba(255,59,48,0.18)"><Candidates values={[3, 5, 6]} strike={[3]} /></Cell>
    </Grid>
  );
}

// LEVEL 10 — XYZ-Wing
function XYZWing() {
  return (
    <Grid>
      <Cell r={4} c={4} fill="rgba(175,82,222,0.30)"><Candidates values={[1, 2, 3]} highlight={[1, 2, 3]} /></Cell>
      <Cell r={4} c={6} fill="rgba(0,122,255,0.22)"><Candidates values={[1, 3]} highlight={[1, 3]} /></Cell>
      <Cell r={6} c={4} fill="rgba(0,122,255,0.22)"><Candidates values={[2, 3]} highlight={[2, 3]} /></Cell>
      {/* Elimination cell — sees all three */}
      {[3, 4, 5].map((r) =>
        [3, 4, 5].map((c) => (
          <Cell key={`box-${r}-${c}`} r={r} c={c} fill={r === 4 && c === 4 ? undefined : 'rgba(175,82,222,0.04)'} />
        ))
      )}
      <Cell r={6} c={6} fill="rgba(255,59,48,0.15)"><Candidates values={[3, 4, 7]} strike={[3]} /></Cell>
    </Grid>
  );
}

// LEVEL 11 — Skyscraper
function Skyscraper() {
  return (
    <Grid>
      {Array.from({ length: 9 }, (_, r) => (
        <Cell key={`col-${r}`} r={r} c={2} fill="rgba(175,82,222,0.08)" />
      ))}
      <Cell r={1} c={2} fill="rgba(52,199,89,0.25)"><Candidates values={[6]} highlight={[6]} /></Cell>
      <Cell r={1} c={7} fill="rgba(255,159,10,0.25)"><Candidates values={[6]} highlight={[6]} /></Cell>
      <Cell r={4} c={2} fill="rgba(52,199,89,0.25)"><Candidates values={[6]} highlight={[6]} /></Cell>
      <Cell r={4} c={5} fill="rgba(255,159,10,0.25)"><Candidates values={[6]} highlight={[6]} /></Cell>
      {/* Elimination: any cell seeing both roofs (r=1,c=7) and (r=4,c=5) */}
      <Cell r={1} c={5} fill="rgba(255,59,48,0.12)"><Candidates values={[2, 6, 9]} strike={[6]} /></Cell>
      <Cell r={4} c={7} fill="rgba(255,59,48,0.12)"><Candidates values={[6, 8]} strike={[6]} /></Cell>
    </Grid>
  );
}

// LEVEL 12 — 2-String Kite
function TwoStringKite() {
  return (
    <Grid>
      {/* Row 2 and Column 3 highlight */}
      {Array.from({ length: 9 }, (_, c) => (
        <Cell key={`r-${c}`} r={2} c={c} fill="rgba(0,122,255,0.06)" />
      ))}
      {Array.from({ length: 9 }, (_, r) => (
        <Cell key={`col-${r}`} r={r} c={3} fill="rgba(255,159,10,0.06)" />
      ))}
      {/* Box (0-2, 3-5) */}
      {[0, 1, 2].map((r) =>
        [3, 4, 5].map((c) => (
          <Cell key={`box-${r}-${c}`} r={r} c={c} fill="rgba(175,82,222,0.08)" />
        ))
      )}
      <Cell r={2} c={4} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={2} c={8} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={1} c={3} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={7} c={3} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={7} c={8} fill="rgba(255,59,48,0.15)"><Candidates values={[2, 4, 6]} strike={[4]} /></Cell>
    </Grid>
  );
}

// LEVEL 13 — W-Wing
function WWing() {
  return (
    <Grid>
      {/* Column 5 = the strong link */}
      {Array.from({ length: 9 }, (_, r) => (
        <Cell key={`col-${r}`} r={r} c={5} fill="rgba(0,122,255,0.06)" />
      ))}
      <Cell r={2} c={2} fill="rgba(175,82,222,0.22)"><Candidates values={[4, 7]} highlight={[4, 7]} /></Cell>
      <Cell r={7} c={7} fill="rgba(175,82,222,0.22)"><Candidates values={[4, 7]} highlight={[4, 7]} /></Cell>
      <Cell r={2} c={5} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={7} c={5} fill="rgba(52,199,89,0.25)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={2} c={7} fill="rgba(255,59,48,0.15)"><Candidates values={[3, 7]} strike={[7]} /></Cell>
      <Cell r={7} c={2} fill="rgba(255,59,48,0.15)"><Candidates values={[1, 7, 9]} strike={[7]} /></Cell>
    </Grid>
  );
}

// LEVEL 14 — Simple Coloring
function SimpleColoring() {
  const blue = [[0, 0], [3, 4], [6, 8]];
  const green = [[0, 4], [3, 8], [6, 0]];
  return (
    <Grid>
      {blue.map(([r, c]) => (
        <Cell key={`b-${r}-${c}`} r={r} c={c} fill="rgba(0,122,255,0.30)">
          <Candidates values={[5]} highlight={[5]} />
        </Cell>
      ))}
      {green.map(([r, c]) => (
        <Cell key={`g-${r}-${c}`} r={r} c={c} fill="rgba(52,199,89,0.30)">
          <Candidates values={[5]} highlight={[5]} />
        </Cell>
      ))}
      {/* Cell that sees both colors → eliminate */}
      <Cell r={3} c={0} fill="rgba(255,59,48,0.15)"><Candidates values={[5, 7]} strike={[5]} /></Cell>
    </Grid>
  );
}

// LEVEL 15 — AIC
function AIC() {
  return (
    <Grid>
      {/* Chain endpoints */}
      <Cell r={0} c={0} fill="rgba(175,82,222,0.25)"><Candidates values={[9]} highlight={[9]} /></Cell>
      <Cell r={0} c={6} fill="rgba(0,122,255,0.18)"><Candidates values={[9]} /></Cell>
      <Cell r={3} c={6} fill="rgba(0,122,255,0.18)"><Candidates values={[9]} /></Cell>
      <Cell r={3} c={2} fill="rgba(175,82,222,0.25)"><Candidates values={[9]} highlight={[9]} /></Cell>
      {/* Chain visualization line */}
      <line
        x1={0.5 * CELL + 1} y1={0.5 * CELL + 1}
        x2={6.5 * CELL + 1} y2={0.5 * CELL + 1}
        stroke="#AF52DE" strokeWidth="2" strokeDasharray="4,2" opacity="0.5"
      />
      <line
        x1={6.5 * CELL + 1} y1={0.5 * CELL + 1}
        x2={6.5 * CELL + 1} y2={3.5 * CELL + 1}
        stroke="#8B5A3C" strokeWidth="2" strokeDasharray="4,2" opacity="0.5"
      />
      <line
        x1={6.5 * CELL + 1} y1={3.5 * CELL + 1}
        x2={2.5 * CELL + 1} y2={3.5 * CELL + 1}
        stroke="#AF52DE" strokeWidth="2" strokeDasharray="4,2" opacity="0.5"
      />
      {/* Elimination cell */}
      <Cell r={3} c={0} fill="rgba(255,59,48,0.15)"><Candidates values={[2, 9]} strike={[9]} /></Cell>
    </Grid>
  );
}

// LEVEL 16 — ALS
function ALS() {
  return (
    <Grid>
      {/* ALS A: row 0, cells 0-1 with candidates {2,3,5} */}
      <Cell r={0} c={0} fill="rgba(0,122,255,0.22)"><Candidates values={[2, 3, 5]} highlight={[2, 3, 5]} /></Cell>
      <Cell r={0} c={1} fill="rgba(0,122,255,0.22)"><Candidates values={[2, 3, 5]} highlight={[2, 3, 5]} /></Cell>
      {/* ALS B: col 0, cells r3-5 with candidates {3,5,7,9} */}
      <Cell r={3} c={0} fill="rgba(255,159,10,0.22)"><Candidates values={[3, 5, 7, 9]} highlight={[3, 5, 7, 9]} /></Cell>
      <Cell r={4} c={0} fill="rgba(255,159,10,0.22)"><Candidates values={[3, 5, 7, 9]} highlight={[3, 5, 7, 9]} /></Cell>
      <Cell r={5} c={0} fill="rgba(255,159,10,0.22)"><Candidates values={[3, 5, 7, 9]} highlight={[3, 5, 7, 9]} /></Cell>
      {/* Elimination */}
      <Cell r={1} c={1} fill="rgba(255,59,48,0.15)"><Candidates values={[1, 5, 8]} strike={[5]} /></Cell>
    </Grid>
  );
}

// LEVEL 17 — Forcing Chain
function ForcingChain() {
  return (
    <Grid>
      <Cell r={2} c={2} fill="rgba(175,82,222,0.25)"><Candidates values={[4, 8]} highlight={[4, 8]} /></Cell>
      <Cell r={2} c={6} fill="rgba(0,122,255,0.18)"><Candidates values={[9]} highlight={[9]} /></Cell>
      <Cell r={6} c={2} fill="rgba(0,122,255,0.18)"><Candidates values={[4]} highlight={[4]} /></Cell>
      <Cell r={4} c={4} fill="rgba(52,199,89,0.28)"><Result value={6} /></Cell>
      {/* Chain lines */}
      <line
        x1={2.5 * CELL + 1} y1={2.5 * CELL + 1}
        x2={4.5 * CELL + 1} y2={4.5 * CELL + 1}
        stroke="#AF52DE" strokeWidth="2" strokeDasharray="3,3" opacity="0.5"
      />
      <line
        x1={2.5 * CELL + 1} y1={2.5 * CELL + 1}
        x2={6.5 * CELL + 1} y2={2.5 * CELL + 1}
        stroke="#8B5A3C" strokeWidth="2" strokeDasharray="3,3" opacity="0.5"
      />
      <line
        x1={2.5 * CELL + 1} y1={2.5 * CELL + 1}
        x2={2.5 * CELL + 1} y2={6.5 * CELL + 1}
        stroke="#8B5A3C" strokeWidth="2" strokeDasharray="3,3" opacity="0.5"
      />
    </Grid>
  );
}

const ILLUSTRATIONS = {
  0: { component: Candidates0, caption: 'The highlighted cell at Row 1, Column 3 has only three legal values left after scanning its row, column, and 3×3 box: {1, 2, 4}. Those three numbers are its candidates. Every empty cell has a candidate set like this — tracking them is what every technique above builds on.' },
  1: { component: NakedSingle, caption: 'Only one candidate remains for the highlighted cell after scanning the row, column, and box. That candidate must be placed there.' },
  2: { component: HiddenSingle, caption: 'Within the highlighted row, the digit 8 can only go in one cell — even though that cell has other candidates, 8 is "hidden" as the only place for it.' },
  3: { component: NakedPair, caption: 'Two cells share exactly the same two candidates {3, 7}. Those two values must occupy those two cells in some order, so 3 and 7 can be eliminated from every other cell in the row.' },
  4: { component: HiddenPair, caption: 'Candidates 2 and 6 appear only in the two highlighted cells of this row. Therefore those two cells must contain 2 and 6 — all other candidates in them can be removed.' },
  5: { component: PointingPair, caption: 'Within the purple box, the candidate 7 appears only in one row. Because 7 must go somewhere in the box, it must go in that row — so 7 can be eliminated from the rest of the row outside the box.' },
  6: { component: BoxLineReduction, caption: 'Within the orange column, candidate 8 appears only in cells belonging to the same 3×3 box. So 8 must land in one of those cells — eliminate 8 from all other cells of that box.' },
  7: { component: XWing, caption: 'Candidate 5 appears exactly twice in rows 2 and 7, and those four cells line up in columns 3 and 8 — forming a rectangle. 5 must occupy two diagonally opposite corners, so it can be eliminated from all other cells of columns 3 and 8.' },
  8: { component: Swordfish, caption: 'Candidate 2 appears only in columns {2, 5, 7} across rows 1, 5, and 8. Each of those three rows must place a 2 in one of those columns, so 2 can be eliminated from every other cell of columns 2, 5, and 7.' },
  9: { component: XYWing, caption: 'Pivot (purple) = {1,2}. Wing A = {1,3}. Wing B = {2,3}. Whichever value the pivot takes, one wing must be 3 — so any cell seeing BOTH wings cannot hold 3.' },
  10: { component: XYZWing, caption: 'Three-cell pattern: pivot {1,2,3}, one wing {1,3}, one wing {2,3}. A cell that sees the pivot and both wings cannot contain 3 — one of the three must be 3.' },
  11: { component: Skyscraper, caption: 'Candidate 6 appears only twice in rows 2 and 5, sharing column 3 (the base). The other two cells (roofs) form a strong link — 6 must appear in one of them. Any cell seeing both roofs has 6 eliminated.' },
  12: { component: TwoStringKite, caption: 'Candidate 4 has a row-link and a column-link that cross inside one box. The two "tails" outside the box form a strong pair — one of them must be 4. Any cell seeing both tails has 4 eliminated.' },
  13: { component: WWing, caption: 'Two bi-value cells {4,7} connected by a strong link on 4 in column 5. One of the two bi-value cells must be 7 — so any cell seeing both loses 7 as a candidate.' },
  14: { component: SimpleColoring, caption: 'Colour alternating strong-link partners blue and green. If two same-coloured cells share a unit, that colour is impossible and the candidate is eliminated from every cell of that colour. Any uncoloured cell seeing both colours also loses the candidate.' },
  15: { component: AIC, caption: 'A chain alternates strong (purple) and weak (blue) links. At least one of the chain endpoints must be true, so any cell that sees both endpoints loses the candidate.' },
  16: { component: ALS, caption: 'ALS A (2 cells, 3 candidates) and ALS B (3 cells, 4 candidates) share the Restricted Common Candidate 3. Another shared candidate — 5 — is therefore eliminated from cells that see all 5-positions in both ALSs.' },
  17: { component: ForcingChain, caption: 'Hypothesise each candidate of a bi-value cell in turn. If every hypothesis forces the same value into another cell, that value is confirmed. The green cell here is forced to 6 by every possible path.' },
};

export default function TutorialIllustration({ techniqueId, showCandidates = true }) {
  const entry = ILLUSTRATIONS[techniqueId];
  if (!entry) return null;
  const Component = entry.component;
  return (
    <CandidatesContext.Provider value={showCandidates}>
      <div className="space-y-3" data-testid={`tutorial-illustration-${techniqueId}`}>
        <Component />
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">
          {entry.caption}
        </p>
      </div>
    </CandidatesContext.Provider>
  );
}
